import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { api, formatApiErrorDetail, trackEvent } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";

export function PartnerForm() {
  const { L } = useLang();
  const F = L.forms;
  const STEPS = F.steps;
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "",
    company_name: "", website: "", social_media: "", business_type: "",
    primary_markets: "", estimated_monthly_leads: "", existing_broker_relationships: "", audience_size: "",
    additional_information: "", consent: false, honeypot: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    if (step === 0 && (!form.full_name || !form.email)) return toast.error(F.consentError);
    if (step === 0) trackEvent("partner_application_start");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.consent) return toast.error(F.consentError);
    setLoading(true);
    try {
      await api.post("/partner-applications", form);
      trackEvent("partner_application_submit");
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="rounded-xl border border-hair bg-white p-10 text-center shadow-sm" data-testid="partner-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
        <h3 className="mt-4 font-heading text-2xl font-semibold text-ink">{F.partnerSuccessTitle}</h3>
        <p className="mt-2 text-subtle">{F.partnerSuccessMsg}</p>
        <Button asChild className="mt-6 bg-finblue hover:bg-finblueDark"><Link to="/">{F.returnHome}</Link></Button>
      </div>
    );

  return (
    <div className="rounded-xl border border-hair bg-white p-6 lg:p-8 shadow-sm" data-testid="partner-form">
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)} />
      <div className="mb-6">
        <div className="flex justify-between text-xs font-medium text-subtle">
          <span>{F.stepOf.replace("{a}", step + 1).replace("{b}", STEPS.length)}</span>
          <span>{STEPS[step]}</span>
        </div>
        <Progress value={((step + 1) / STEPS.length) * 100} className="mt-2 h-1.5" />
      </div>

      {step === 0 && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Fld label={F.fullName} req><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="partner-name" /></Fld>
          <Fld label={F.email} req><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="partner-email" /></Fld>
          <Fld label={F.phone}><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="partner-phone" /></Fld>
          <Fld label={F.country}><Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="partner-country" /></Fld>
        </div>
      )}
      {step === 1 && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Fld label={F.company}><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="partner-company" /></Fld>
          <Fld label={F.website}><Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="partner-website" /></Fld>
          <Fld label={F.social}><Input value={form.social_media} onChange={(e) => set("social_media", e.target.value)} data-testid="partner-social" /></Fld>
          <Fld label={F.businessType}><Input value={form.business_type} onChange={(e) => set("business_type", e.target.value)} data-testid="partner-type" /></Fld>
        </div>
      )}
      {step === 2 && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Fld label={F.primaryMarkets}><Input value={form.primary_markets} onChange={(e) => set("primary_markets", e.target.value)} data-testid="partner-markets" /></Fld>
          <Fld label={F.monthlyLeads}><Input value={form.estimated_monthly_leads} onChange={(e) => set("estimated_monthly_leads", e.target.value)} data-testid="partner-leads" /></Fld>
          <Fld label={F.brokerRel}><Input value={form.existing_broker_relationships} onChange={(e) => set("existing_broker_relationships", e.target.value)} data-testid="partner-brokers" /></Fld>
          <Fld label={F.audience}><Input value={form.audience_size} onChange={(e) => set("audience_size", e.target.value)} data-testid="partner-audience" /></Fld>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-5">
          <Fld label={F.additional}><Textarea rows={4} value={form.additional_information} onChange={(e) => set("additional_information", e.target.value)} data-testid="partner-notes" /></Fld>
          <label className="flex items-start gap-3 text-sm text-subtle">
            <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="partner-consent" className="mt-0.5" />
            <span>{F.partnerConsent}</span>
          </label>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="partner-back"><ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {L.common.back}</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next} className="bg-finblue hover:bg-finblueDark" data-testid="partner-next">{L.common.continue} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Button>
        ) : (
          <Button onClick={submit} disabled={loading} className="bg-finblue hover:bg-finblueDark" data-testid="partner-submit">{loading ? F.submitting : F.submitApp}</Button>
        )}
      </div>
    </div>
  );
}

function Fld({ label, req, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-ink">{label}{req && <span className="text-danger"> *</span>}</Label>
      {children}
    </div>
  );
}
